import { ConditionalCondition, ConditionalDefault, ConditionalOperator, operatorRequiresValue } from '../models/templating/api/ConditionalDefaultModel';
import TemplateFormFieldModel from '../models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldValueModel from '../models/templating/api/TemplateFormFieldValueModel';
import { findFieldValueForField } from './templateFieldMatching';

/**
 * Evaluates a single operator condition against two string values.
 */
const evaluateOperator = (
    fieldValue: string | undefined,
    operator: ConditionalOperator,
    comparisonValue: string | undefined
): boolean => {
    const normalizedField = (fieldValue ?? '').trim();
    const normalizedComparison = (comparisonValue ?? '').trim();

    switch (operator) {
        case 'equals':
            return normalizedField === normalizedComparison;
        case 'notEquals':
            return normalizedField !== normalizedComparison;
        case 'contains':
            return normalizedField.toLowerCase().includes(normalizedComparison.toLowerCase());
        case 'startsWith':
            return normalizedField.toLowerCase().startsWith(normalizedComparison.toLowerCase());
        case 'isEmpty':
            return normalizedField === '';
        case 'isNotEmpty':
            return normalizedField !== '';
        case 'greaterThan': {
            const a = parseFloat(normalizedField);
            const b = parseFloat(normalizedComparison);
            return !isNaN(a) && !isNaN(b) && a > b;
        }
        case 'lessThan': {
            const a = parseFloat(normalizedField);
            const b = parseFloat(normalizedComparison);
            return !isNaN(a) && !isNaN(b) && a < b;
        }
        default:
            return false;
    }
};

/**
 * Builds a lookup map of associatedVariable -> current value from a set of
 * fields and their recorded values. Pre-computing this once and passing it to
 * `evaluateConditionalDefault` avoids rebuilding an O(N) map for every field
 * in the form when evaluating the whole set in a loop.
 */
export const buildValueByAssociatedVariable = (
    allFields: TemplateFormFieldModel[],
    allValues: TemplateFormFieldValueModel[]
): Record<string, string | undefined> => {
    const map: Record<string, string | undefined> = {};
    allFields.forEach(f => {
        if (f.associatedVariable) {
            const found = f.name
                ? allValues?.find(v => v.dynamicFormFieldName === f.name)
                : findFieldValueForField(f, allValues);
            map[f.associatedVariable] = found?.value;
        }
    });
    return map;
};

/**
 * Given all current field values and a set of fields (from the same form),
 * evaluates the conditionalDefaults on `targetField` and returns the matched
 * default value string, or undefined if no condition matches.
 *
 * Falls back to `targetField.defaultValue` if no condition matches and no
 * `otherwise` is set.
 *
 * @param prebuiltValueMap - Optional pre-computed map of associatedVariable ->
 *   value.  When processing many fields in a loop, build this once with
 *   `buildValueByAssociatedVariable` and pass it here to avoid O(N²) work.
 *   When omitted, the map is built internally from `allFields` + `allValues`.
 */
export const evaluateConditionalDefault = (
    targetField: TemplateFormFieldModel,
    allFields: TemplateFormFieldModel[],
    allValues: TemplateFormFieldValueModel[],
    prebuiltValueMap?: Record<string, string | undefined>
): string | undefined => {
    const conditionalDefaults = targetField.conditionalDefaults;

    if (!conditionalDefaults || !conditionalDefaults.conditions?.length) {
        return targetField.defaultValue;
    }

    // Use the caller-supplied map when available to avoid rebuilding it for
    // every field in a loop; otherwise build it inline (backward-compatible).
    const valueByAssociatedVariable = prebuiltValueMap ?? buildValueByAssociatedVariable(allFields, allValues);

    for (const condition of conditionalDefaults.conditions) {
        const currentValue = valueByAssociatedVariable[condition.fieldAssociatedVariable];
        const matches = evaluateOperator(
            currentValue,
            condition.operator,
            operatorRequiresValue(condition.operator) ? condition.value : undefined
        );
        if (matches) {
            return condition.defaultValue;
        }
    }

    // No condition matched - use `otherwise` if non-empty, else fall back to static default.
    // An empty-string `otherwise` is treated as "not set" so that the static defaultValue
    // is used as a fallback (an empty otherwise is almost always unintentional — the editor
    // initialises it to '' on first enable before the user has typed anything).
    return (conditionalDefaults.otherwise !== undefined && conditionalDefaults.otherwise !== '')
        ? conditionalDefaults.otherwise
        : targetField.defaultValue;
};

/**
 * Resolves conditional (and static) defaults for all field value records that
 * have an empty/missing value, just before the form is saved or submitted.
 *
 * This ensures that a field whose effective default was only shown as a visual
 * placeholder (never manually edited by the user) is persisted with the correct
 * resolved value rather than as blank.
 *
 * Fields that already have a non-empty user-supplied value are left untouched.
 */
export const resolveConditionalDefaultsForSubmit = (
    allFields: TemplateFormFieldModel[],
    fieldValues: TemplateFormFieldValueModel[]
): TemplateFormFieldValueModel[] => {
    return fieldValues.map(valueRecord => {
        // Find the matching field definition
        const field = allFields.find(f =>
            (f.name && f.name === valueRecord.dynamicFormFieldName) ||
            (!f.name && findFieldValueForField(f, [valueRecord]) === valueRecord)
        );
        if (!field) return valueRecord;

        if (field.conditionalDefaults?.conditions?.length) {
            // Field has conditional defaults — only fill in blank values.
            // A non-empty user-supplied value is always left untouched so that
            // fields with conditional defaults remain editable after the user
            // has typed something.
            if (valueRecord.value !== undefined && valueRecord.value !== null && valueRecord.value.trim() !== '') {
                return valueRecord;
            }
            const resolved = evaluateConditionalDefault(field, allFields, fieldValues);
            if (resolved !== undefined && resolved.trim() !== '') {
                return { ...valueRecord, value: resolved };
            }
            // The resolved value is empty/undefined (e.g. the fallback default is
            // intentionally blank). Normalise any whitespace-only stored value
            // (such as the "\n" a blank LongTextField contentEditable produces)
            // to an empty string so it is persisted correctly.
            if (valueRecord.value !== undefined && valueRecord.value !== null && valueRecord.value.trim() === '') {
                return { ...valueRecord, value: '' };
            }
            return valueRecord;
        }

        // No conditional defaults — only fill in blanks with the static default.
        if (valueRecord.value !== undefined && valueRecord.value !== null && valueRecord.value.trim() !== '') {
            return valueRecord;
        }
        const resolved = evaluateConditionalDefault(field, allFields, fieldValues);
        if (resolved !== undefined && resolved.trim() !== '') {
            return { ...valueRecord, value: resolved };
        }
        // No static default either — normalise whitespace-only values (e.g. "\n")
        // to empty string so they are persisted correctly.
        if (valueRecord.value !== undefined && valueRecord.value !== null && valueRecord.value.trim() === '') {
            return { ...valueRecord, value: '' };
        }
        return valueRecord;
    });
};

/**
 * Returns all validation errors for a ConditionalDefault config.
 * Returns an empty array if the config is valid.
 */
export const validateConditionalDefaults = (
    conditionalDefaults: ConditionalDefault | undefined
): string[] => {
    if (!conditionalDefaults) return [];

    const errors: string[] = [];
    const seen = new Set<string>();

    conditionalDefaults.conditions.forEach((condition, index) => {
        const rowLabel = `Condition ${index + 1}`;

        if (!condition.fieldAssociatedVariable) {
            errors.push(`${rowLabel}: No field selected.`);
        }

        if (operatorRequiresValue(condition.operator) && !condition.value?.trim()) {
            errors.push(`${rowLabel}: A comparison value is required for "${condition.operator}".`);
        }

        // Duplicate detection: same field + operator + value
        const key = `${condition.fieldAssociatedVariable}|${condition.operator}|${condition.value ?? ''}`;
        if (seen.has(key)) {
            errors.push(`${rowLabel}: Duplicate condition (same field, operator, and value already exists).`);
        } else {
            seen.add(key);
        }
    });

    return errors;
};
